import { Icon, IconPrivacyOptionsDefault, Separator } from '@mc/design-system'
import { useFeatureFlips } from '@mc/feature-flip-react'
import { isGeo } from '@mc/geo'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { useStaticData } from '@mc/static-data-react'
import AppStore from 'components/AppStore'
import Link from 'components/Link'
import {
  amazonPath,
  articlesPath,
  businessPath,
  caPrivacyPath,
  deiPath,
  doNotSellPath,
  facebookPath,
  giftPath,
  googlePlayPath,
  grantsPath,
  homePath,
  instagramPath,
  itunesPath,
  learnerGuidelinesPath,
  linkedInPath,
  newsroomPath,
  podcastPath,
  privacyPath,
  rokuPath,
  securityPath,
  sitemapPath,
  termsPath,
  twitterPath,
  youtubePath,
} from 'libs/paths'
import { FooterEmailCaptureSection } from './FooterEmailCaptureSection'
import { trackNavigationClick } from './events/footerNavigationEvents'
import Logo from './logo.svg'

const copyrightInfo = `\u00A9 ${new Date().getFullYear()} MasterClass`

export const RegularFooter = () => {
  const { t } = useTranslation('@mc/next')
  // Logged-out-only gate: the footer email capture must never render for
  // signed-in members. Do not collapse this check away.
  const { isLoggedIn } = useStaticData<{ isLoggedIn: boolean }>()
  const showEmailCapture = !isLoggedIn
  // https://app.growthbook.io/features/footer_podcast_link
  const { footer_podcast_link: showPodcastLink } = useFeatureFlips()

  const navColumns = (
    <>
      <div className='col' aria-labelledby='footer-explore'>
        <h3
          id='footer-explore'
          className='mc-text-h7 mc-opacity--muted mc-mb-6'
        >
          {t('footer.columns.explore.heading')}
        </h3>
        <ul className='mc-text-small mc-mb-9'>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={articlesPath()}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.explore.articles'))
              }
            >
              {t('footer.columns.explore.articles')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={sitemapPath()}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.explore.sitemap'))
              }
            >
              {t('footer.columns.explore.sitemap')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={giftPath()}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.explore.gifts'))
              }
            >
              {t('footer.columns.explore.gifts')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={paths.memberWins}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.explore.memberWins'))
              }
            >
              {t('footer.columns.explore.memberWins')}
            </Link>
          </li>
          {showPodcastLink && (
            <li className='mc-mb-5 mc-pb-1'>
              <Link
                href={podcastPath()}
                className='mc-text--bare-link'
                newTab
                isBasic
                onClick={() =>
                  trackNavigationClick(t('footer.columns.explore.podcast'))
                }
              >
                {t('footer.columns.explore.podcast')}
              </Link>
            </li>
          )}
        </ul>
      </div>
      <div className='col' aria-labelledby='footer-about'>
        <h3 id='footer-about' className='mc-text-h7 mc-opacity--muted mc-mb-6'>
          {t('footer.columns.about.heading')}
        </h3>
        <ul className='mc-text-small mc-mb-9'>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={deiPath()}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.about.diversity'))
              }
            >
              {t('footer.columns.about.diversity')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={paths.careers}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.about.careers'))
              }
            >
              {t('footer.columns.about.careers')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={newsroomPath()}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.explore.newsroom'))
              }
            >
              {t('footer.columns.explore.newsroom')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={securityPath()}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.about.security'))
              }
            >
              {t('footer.columns.about.security')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={privacyPath()}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.about.privacy'))
              }
            >
              {t('footer.columns.about.privacy')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={learnerGuidelinesPath()}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(
                  t('footer.columns.about.learnerGuidelines'),
                )
              }
            >
              {t('footer.columns.about.learnerGuidelines')}
            </Link>
          </li>
          {isGeo('US') && (
            <>
              <li className='mc-mb-5 mc-pb-1'>
                <Link
                  href={caPrivacyPath()}
                  className='mc-text--bare-link'
                  passHref
                  onClick={() =>
                    trackNavigationClick(t('footer.columns.about.caPrivacy'))
                  }
                >
                  {t('footer.columns.about.caPrivacy')}
                </Link>
              </li>
              <li className='mc-mb-5 mc-pb-1'>
                <Link
                  href={doNotSellPath()}
                  className='mc-text--bare-link'
                  passHref
                  onClick={() =>
                    trackNavigationClick(t('footer.columns.about.doNotSell'))
                  }
                >
                  {t('footer.columns.about.doNotSell')}
                  <IconPrivacyOptionsDefault
                    size='lg'
                    className='mc-overflow--visible mc-ml-1'
                    width='30'
                    height='15'
                    viewBox='0 0 30 15'
                  />
                </Link>
              </li>
            </>
          )}
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={termsPath()}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.about.terms'))
              }
            >
              {t('footer.columns.about.terms')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={grantsPath()}
              className='mc-text--bare-link'
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.about.grants'))
              }
            >
              {t('footer.columns.about.grants')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={businessPath()}
              className='mc-text--bare-link'
              passHref
              onClick={() =>
                trackNavigationClick(t('footer.columns.about.business'))
              }
            >
              {t('footer.columns.about.business')}
            </Link>
          </li>
          <li className='mc-mb-5 mc-pb-1'>
            <Link
              href={paths.helpCenter}
              className='mc-text--bare-link'
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.about.support'))
              }
            >
              {t('footer.columns.about.support')}
            </Link>
          </li>
        </ul>
      </div>
      <div className='col d-none d-md-block' aria-labelledby='footer-social'>
        <h3 id='footer-social' className='mc-text-h7 mc-opacity--muted mc-mb-6'>
          {t('footer.columns.social.heading')}
        </h3>
        <ul className='mc-text-small mc-mb-9'>
          <li className='mc-mb-4'>
            <Link
              href={twitterPath()}
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.social.twitter'))
              }
            >
              <Icon
                className='mc-icon--3 mc-opacity--muted mc-mr-4'
                kind='twitter'
              />
              <span className='mc-text--bare-link'>
                {t('footer.columns.social.twitter')}
              </span>
            </Link>
          </li>
          <li className='mc-mb-4'>
            <Link
              href={instagramPath()}
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.social.instagram'))
              }
            >
              <Icon
                className='mc-icon--3 mc-opacity--muted mc-mr-4'
                kind='instagram'
              />
              <span className='mc-text--bare-link'>
                {t('footer.columns.social.instagram')}
              </span>
            </Link>
          </li>
          <li className='mc-mb-4'>
            <Link
              href={facebookPath()}
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.social.facebook'))
              }
            >
              <Icon
                className='mc-icon--3 mc-opacity--muted mc-mr-4'
                kind='facebook'
              />
              <span className='mc-text--bare-link'>
                {t('footer.columns.social.facebook')}
              </span>
            </Link>
          </li>
          <li className='mc-mb-4'>
            <Link
              href={youtubePath()}
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.social.youtube'))
              }
            >
              <Icon
                className='mc-icon--3 mc-opacity--muted mc-mr-4'
                kind='youtube'
              />
              <span className='mc-text--bare-link'>
                {t('footer.columns.social.youtube')}
              </span>
            </Link>
          </li>
          <li className='mc-mb-4'>
            <Link
              href={linkedInPath()}
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.social.linkedin'))
              }
            >
              <Icon
                className='mc-icon--3 mc-opacity--muted mc-mr-4'
                kind='linked-in'
              />
              <span className='mc-text--bare-link'>
                {t('footer.columns.social.linkedin')}
              </span>
            </Link>
          </li>
        </ul>
      </div>
      <div className='d-none d-md-block'>
        <div className='col' aria-labelledby='footer-download'>
          <h3
            id='footer-download'
            className='mc-text-h7 mc-opacity--muted mc-mb-6'
          >
            {t('footer.columns.download.header')}
          </h3>
          <ul className='mc-mb-9'>
            <li className='mc-mb-4'>
              <Link
                href={itunesPath()}
                newTab
                isBasic
                onClick={() => trackNavigationClick('Apple App Store')}
              >
                <AppStore kind='apple' />
              </Link>
            </li>
            <li className='mc-mb-4'>
              <Link
                href={googlePlayPath()}
                newTab
                isBasic
                onClick={() => trackNavigationClick('Google Play')}
              >
                <AppStore kind='google' />
              </Link>
            </li>
            <li className='mc-mb-4'>
              <Link
                href={amazonPath()}
                newTab
                isBasic
                onClick={() => trackNavigationClick('Amazon App Store')}
              >
                <AppStore kind='amazon' />
              </Link>
            </li>
            <li className='mc-mb-4'>
              <Link
                href={rokuPath()}
                newTab
                isBasic
                onClick={() => trackNavigationClick('Roku Channel Store')}
              >
                <AppStore kind='roku' />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </>
  )

  return (
    <footer className='footer mc-bg-dark mc-theme-dark'>
      <Separator className='mc-m-0' />
      <div className='container mc-mt-10'>
        <div className='row'>
          {showEmailCapture && (
            <div className='col-12 col-md-4 mc-mb-9 mc-mb-md-0'>
              <FooterEmailCaptureSection />
            </div>
          )}
          {showEmailCapture ? (
            <div className='col offset-md-1'>
              <div className='row'>{navColumns}</div>
            </div>
          ) : (
            navColumns
          )}
        </div>
        <div className='row d-md-none mc-my-9 justify-content-center'>
          <div className='col-2'>
            <Link
              href={twitterPath()}
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.social.twitter.string'))
              }
            >
              <span className='mc-sr-only'>
                {t('footer.columns.social.twitter.string')}
              </span>
              <Icon
                className='mc-icon--4 mc-opacity--muted mc-mr-4'
                kind='twitter'
              />
            </Link>
          </div>
          <div className='col-2'>
            <Link
              href={facebookPath()}
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.social.facebook.string'))
              }
            >
              <span className='mc-sr-only'>
                {t('footer.columns.social.facebook.string')}
              </span>
              <Icon
                className='mc-icon--4 mc-opacity--muted mc-mr-4'
                kind='facebook'
              />
            </Link>
          </div>
          <div className='col-2'>
            <Link
              href={instagramPath()}
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(
                  t('footer.columns.social.instagram.string'),
                )
              }
            >
              <span className='mc-sr-only'>
                {t('footer.columns.social.instagram.string')}
              </span>
              <Icon
                className='mc-icon--4 mc-opacity--muted mc-mr-4'
                kind='instagram'
              />
            </Link>
          </div>
          <div className='col-2'>
            <Link
              href={youtubePath()}
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.social.youtube.string'))
              }
            >
              <span className='mc-sr-only'>
                {t('footer.columns.social.youtube.string')}
              </span>
              <Icon
                className='mc-icon--4 mc-opacity--muted mc-mr-4'
                kind='youtube'
              />
            </Link>
          </div>
          <div className='col-2'>
            <Link
              href={linkedInPath()}
              newTab
              isBasic
              onClick={() =>
                trackNavigationClick(t('footer.columns.social.linkedin.string'))
              }
            >
              <span className='mc-sr-only'>
                {t('footer.columns.social.linkedin.string')}
              </span>
              <Icon
                className='mc-icon--4 mc-opacity--muted mc-mr-4'
                kind='linked-in'
              />
            </Link>
          </div>
        </div>
        <div className='d-block d-md-none'>
          <div className='row justify-content-center'>
            <div className='mc-mr-2'>
              <Link
                href={itunesPath()}
                newTab
                isBasic
                onClick={() => trackNavigationClick('Apple App Store')}
              >
                <AppStore kind='apple' />
              </Link>
            </div>
            <div className='mc-ml-2'>
              <Link
                href={googlePlayPath()}
                newTab
                isBasic
                onClick={() => trackNavigationClick('Google Play Store')}
              >
                <AppStore kind='google' />
              </Link>
            </div>
          </div>
          <div className='row mc-mt-4 justify-content-center'>
            <div className='mc-mr-2'>
              <Link
                href={amazonPath()}
                newTab
                isBasic
                onClick={() => trackNavigationClick('Amazon App Store')}
              >
                <AppStore kind='amazon' />
              </Link>
            </div>
            <div className='mc-ml-2'>
              <Link
                href={rokuPath()}
                newTab
                isBasic
                onClick={() => trackNavigationClick('Roku Channel Store')}
              >
                <AppStore kind='roku' />
              </Link>
            </div>
          </div>
        </div>
        <div className='row d-none d-md-block mc-mt-10'>
          <div className='col'>
            <div className='d-inline-flex align-items-center mc-my-4'>
              <Link
                href={homePath()}
                className='mc-mr-4'
                passHref
                onClick={() => trackNavigationClick('MC Logo')}
              >
                <Icon kind='logo' />
                <span className='mc-sr-only'>{t('footer.logo')}</span>
              </Link>
              <span className='mc-text-small mc-opacity--muted'>
                {copyrightInfo}
              </span>
              <div className='mc-mx-4 mc-text-small mc-opacity--muted d-flex align-items-center'>
                <Icon className='mc-icon--3' kind='locked' />
                <span>{t('footer.secured')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className='d-md-none mc-mt-10 mc-mb-4'>
          <div className='row justify-content-center'>
            <div className='col-auto'>
              <Link
                href={homePath()}
                passHref
                onClick={() => trackNavigationClick('MC Logo')}
              >
                <Logo
                  className='mc-mb-4'
                  aria-hidden='true'
                  viewBox='0 0 171 130'
                  width={88}
                  height={75}
                />
                <span className='mc-sr-only'>{t('footer.logo')}</span>
              </Link>
            </div>
          </div>

          <div className='mc-text--center mc-text-small mc-opacity--muted'>
            <p>{copyrightInfo}</p>
            <p className='mc-mt-2'>
              <Icon className='mc-icon--3' kind='locked' />
              <span>{t('footer.secured')}</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
